import { useEffect, useRef } from "react";

import { INACTIVITY_TIMEOUT_MS } from "@/constants/auth-session.constants";
import { markUserActivity, subscribeUserActivity } from "@/utils/session-activity";

const ACTIVITY_EVENTS = [
  "keydown",
  "click",
  "scroll",
  "touchstart",
  "mousedown",
  "pointerdown",
] as const;
const POINTER_MOVE_THROTTLE_MS = 5_000;
const IDLE_CHECK_INTERVAL_MS = 10_000;

export function useIdleTimeout(onIdle: () => void, enabled: boolean) {
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  const lastActivityRef = useRef(Date.now());
  const lastPointerMoveRef = useRef(0);

  useEffect(() => {
    if (!enabled) return undefined;

    lastActivityRef.current = Date.now();
    markUserActivity();

    const noteLocalActivity = () => {
      lastActivityRef.current = Date.now();
    };

    const resetTimer = () => {
      noteLocalActivity();
      markUserActivity();
    };

    const onPointerMove = () => {
      const now = Date.now();
      if (now - lastPointerMoveRef.current < POINTER_MOVE_THROTTLE_MS) return;
      lastPointerMoveRef.current = now;
      resetTimer();
    };

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, resetTimer, { passive: true });
    });
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    // Sync from axios/websocket/other modules. Do not broadcast again.
    const unsubscribe = subscribeUserActivity(noteLocalActivity);

    const intervalId = window.setInterval(() => {
      if (Date.now() - lastActivityRef.current >= INACTIVITY_TIMEOUT_MS) {
        onIdleRef.current();
      }
    }, IDLE_CHECK_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
      window.removeEventListener("pointermove", onPointerMove);
      unsubscribe();
    };
  }, [enabled]);
}

export default useIdleTimeout;
